<template>
  <Dialog v-model:open="dialogOpen">
    <DialogContent class="max-h-[92vh] max-w-[44rem] overflow-y-auto rounded-[1.4rem] border border-[rgba(215,226,239,0.95)] bg-white p-0 shadow-[0_24px_60px_rgba(15,23,42,0.22)]">
      <div class="border-b border-[rgba(227,233,243,0.95)] px-6 py-5">
        <DialogHeader class="gap-y-2 text-left">
          <DialogTitle class="text-[1.3rem] font-extrabold text-[#182033]">
            Generate Monthly Progress Report
          </DialogTitle>
          <DialogDescription class="text-[#6e7b92]">
            Confirm the filters, choose optional supporting resources, and open the HTML report or download the matching PDF.
          </DialogDescription>
        </DialogHeader>
      </div>

      <form
        class="space-y-5 px-6 py-5"
        @submit.prevent="submit"
      >
        <div class="grid gap-4 md:grid-cols-2">
          <div class="space-y-2 md:col-span-2">
            <p class="text-[0.75rem] font-extrabold uppercase tracking-[0.08em] text-[#748197]">
              Client
            </p>
            <select
              v-if="canFilterByClient"
              v-model="form.client_id"
              class="h-11 w-full rounded-[0.95rem] border border-[rgba(211,220,233,1)] bg-white px-3 text-sm text-[#243049] focus:border-[#8eb4ff] focus:outline-none focus:ring-2 focus:ring-[#cfe0ff]"
            >
              <option value="all">
                All Clients
              </option>
              <option
                v-for="client in clients"
                :key="client.id"
                :value="client.client_id"
              >
                {{ client.company_name }}
              </option>
            </select>
            <div
              v-else
              class="rounded-[0.95rem] border border-[rgba(211,220,233,1)] bg-[#f8fbff] px-3 py-3 text-sm text-[#243049]"
            >
              {{ currentClientLabel }}
            </div>
          </div>

          <div class="space-y-2">
            <p class="text-[0.75rem] font-extrabold uppercase tracking-[0.08em] text-[#748197]">
              Package
            </p>
            <select
              v-model="packageValue"
              class="h-11 w-full rounded-[0.95rem] border border-[rgba(211,220,233,1)] bg-white px-3 text-sm text-[#243049] focus:border-[#8eb4ff] focus:outline-none focus:ring-2 focus:ring-[#cfe0ff]"
            >
              <option value="">
                All Packages
              </option>
              <option
                v-for="pkg in packageOptions"
                :key="pkg.value"
                :value="pkg.value"
              >
                {{ pkg.label }}
              </option>
            </select>
          </div>

          <div class="space-y-2">
            <p class="text-[0.75rem] font-extrabold uppercase tracking-[0.08em] text-[#748197]">
              Service
            </p>
            <select
              v-model="serviceValue"
              class="h-11 w-full rounded-[0.95rem] border border-[rgba(211,220,233,1)] bg-white px-3 text-sm text-[#243049] focus:border-[#8eb4ff] focus:outline-none focus:ring-2 focus:ring-[#cfe0ff]"
            >
              <option value="">
                All Services
              </option>
              <option
                v-for="service in serviceOptions"
                :key="service.value"
                :value="service.value"
              >
                {{ service.label }}
              </option>
            </select>
          </div>

          <div class="space-y-2">
            <p class="text-[0.75rem] font-extrabold uppercase tracking-[0.08em] text-[#748197]">
              Date From
            </p>
            <Input
              v-model="form.date_from"
              type="date"
              class="h-11 rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white text-sm text-[#243049] focus-visible:ring-[#8eb4ff] focus-visible:ring-offset-0"
            />
          </div>

          <div class="space-y-2">
            <p class="text-[0.75rem] font-extrabold uppercase tracking-[0.08em] text-[#748197]">
              Date To
            </p>
            <Input
              v-model="form.date_to"
              type="date"
              class="h-11 rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white text-sm text-[#243049] focus-visible:ring-[#8eb4ff] focus-visible:ring-offset-0"
            />
          </div>
        </div>

        <div class="rounded-[1.15rem] border border-[rgba(211,220,233,1)] bg-[#f8fbff] p-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-[0.88rem] font-extrabold text-[#182033]">
                Supporting Resources
              </p>
              <p class="mt-1 text-[0.9rem] leading-6 text-[#6e7b92]">
                Optional PDFs or images that can be referenced in the appendix.
              </p>
            </div>
            <label class="flex items-center gap-2 text-sm font-semibold text-[#243049]">
              <input
                v-model="form.include_appendix"
                type="checkbox"
                class="h-4 w-4 rounded border-[#c1cfe1] text-[#2f6fab] focus:ring-[#8eb4ff]"
              >
              Include appendix
            </label>
          </div>

          <div class="mt-4">
            <input
              type="file"
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.webp"
              class="block w-full cursor-pointer rounded-[0.95rem] border border-dashed border-[#c3d2e7] bg-white px-3 py-3 text-sm text-[#243049] file:mr-4 file:rounded-full file:border-0 file:bg-[#eaf2fb] file:px-4 file:py-2 file:text-sm file:font-bold file:text-[#2f6fab] hover:file:bg-[#ddeaf8]"
              @change="handleFiles"
            >
            <p class="mt-2 text-xs text-[#6e7b92]">
              Allowed types: PDF, PNG, JPG, JPEG, WEBP.
            </p>
            <div
              v-if="selectedFiles.length"
              class="mt-3 space-y-2"
            >
              <div
                v-for="file in selectedFiles"
                :key="`${file.name}-${file.size}`"
                class="flex items-center justify-between gap-3 rounded-[0.9rem] border border-[rgba(216,228,240,0.95)] bg-white px-3 py-2 text-sm"
              >
                <span class="truncate text-[#243049]">{{ file.name }}</span>
                <span class="whitespace-nowrap text-xs font-semibold text-[#6e7b92]">{{ formatSize(file.size) }}</span>
              </div>
            </div>
            <div
              v-else
              class="mt-3 rounded-[0.9rem] border border-dashed border-[rgba(216,228,240,0.95)] bg-white px-3 py-3 text-sm text-[#6e7b92]"
            >
              No supporting resources uploaded.
            </div>
          </div>
        </div>

        <div class="grid gap-3 md:grid-cols-3">
          <div class="rounded-[1rem] border border-[rgba(216,228,240,0.95)] bg-white px-4 py-3">
            <p class="text-[0.72rem] font-extrabold uppercase tracking-[0.08em] text-[#748197]">
              Client Scope
            </p>
            <p class="mt-1 text-sm font-semibold text-[#243049]">
              {{ summaryClient }}
            </p>
          </div>
          <div class="rounded-[1rem] border border-[rgba(216,228,240,0.95)] bg-white px-4 py-3">
            <p class="text-[0.72rem] font-extrabold uppercase tracking-[0.08em] text-[#748197]">
              Reporting Period
            </p>
            <p class="mt-1 text-sm font-semibold text-[#243049]">
              {{ summaryRange }}
            </p>
          </div>
          <div class="rounded-[1rem] border border-[rgba(216,228,240,0.95)] bg-white px-4 py-3">
            <p class="text-[0.72rem] font-extrabold uppercase tracking-[0.08em] text-[#748197]">
              Appendix
            </p>
            <p class="mt-1 text-sm font-semibold text-[#243049]">
              {{ form.include_appendix ? "Included" : "Disabled" }}
            </p>
          </div>
        </div>

        <div
          v-if="error"
          class="rounded-[1rem] border border-[#f5c2c7] bg-[#fff5f5] px-4 py-3 text-sm text-[#b42318]"
        >
          {{ error }}
        </div>

        <DialogFooter class="border-t border-[rgba(227,233,243,0.95)] px-0 pt-4">
          <Button
            variant="outline"
            type="button"
            class="rounded-[0.95rem] border-[rgba(49,125,255,0.22)] bg-[#f6f9ff] px-4 py-2 font-bold text-[#2855a6] hover:bg-[#eef4ff]"
            @click="close"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            class="rounded-[0.95rem] bg-[#1f7aff] px-4 py-2 font-bold text-white hover:bg-[#1f7aff]"
            :disabled="loading"
            @click="mode = 'html'"
          >
            {{ loading && mode === 'html' ? "Generating..." : "Generate HTML Report" }}
          </Button>
          <Button
            type="submit"
            class="rounded-[0.95rem] bg-[#173b5f] px-4 py-2 font-bold text-white hover:bg-[#173b5f]"
            :disabled="loading"
            @click="mode = 'pdf'"
          >
            {{ loading && mode === 'pdf' ? "Generating..." : "Generate & Download Report" }}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type { DashboardClientOption, DashboardFilters, DashboardReportRequest, DashboardRole } from "@/types/dashboard";

const props = defineProps<{
  open: boolean;
  loading?: boolean;
  error?: string | null;
  role: DashboardRole;
  filters: DashboardFilters;
  clients: DashboardClientOption[];
  serviceOptions: { label: string; value: string }[];
  packageOptions: { label: string; value: string }[];
}>();

const emit = defineEmits<{
  (event: "update:open", value: boolean): void;
  (event: "generate", value: DashboardReportRequest & { mode: "html" | "pdf" }): void;
}>();

const dialogOpen = computed({
  get: () => props.open,
  set: (value: boolean) => emit("update:open", value),
});

const form = reactive<DashboardReportRequest>({
  client_id: "all",
  service_id: null,
  package_id: null,
  date_from: null,
  date_to: null,
  include_appendix: true,
  supporting_resources: [],
});

const selectedFiles = ref<File[]>([]);
const mode = ref<"html" | "pdf">("html");

const canFilterByClient = computed(() => props.role !== "client");
const currentClientLabel = computed(() => {
  const match = props.clients.find((client) => client.client_id === props.filters.client_id);
  return match?.company_name ?? "Current client";
});
const summaryClient = computed(() => {
  if (!canFilterByClient.value) {
    return currentClientLabel.value;
  }

  if (form.client_id === "all") {
    return "All clients";
  }

  return props.clients.find((client) => client.client_id === form.client_id)?.company_name ?? "Selected client";
});

const packageValue = computed({
  get: () => (form.package_id === null ? "" : String(form.package_id)),
  set: (value: string) => {
    form.package_id = value === "" ? null : Number(value);
  },
});

const serviceValue = computed({
  get: () => (form.service_id === null ? "" : String(form.service_id)),
  set: (value: string) => {
    form.service_id = value === "" ? null : Number(value);
  },
});

const summaryRange = computed(() => {
  const from = form.date_from ?? "Start";
  const to = form.date_to ?? "Now";
  return `${from} to ${to}`;
});

function resetFromFilters() {
  form.client_id = props.filters.client_id || "all";
  form.service_id = props.filters.service_id;
  form.package_id = props.filters.package_id;
  form.date_from = props.filters.date_from;
  form.date_to = props.filters.date_to;
  form.include_appendix = true;
  form.supporting_resources = [];
  selectedFiles.value = [];
}

function handleFiles(event: Event) {
  const input = event.target as HTMLInputElement;
  selectedFiles.value = Array.from(input.files ?? []);
}

function submit() {
  emit("generate", {
    client_id: form.client_id,
    service_id: form.service_id,
    package_id: form.package_id,
    date_from: form.date_from,
    date_to: form.date_to,
    include_appendix: form.include_appendix,
    supporting_resources: selectedFiles.value,
    mode: mode.value,
  });
}

function close() {
  emit("update:open", false);
}

function formatSize(size: number) {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      resetFromFilters();
      mode.value = "html";
    }
  },
);
</script>
